import React, { useState, useEffect } from 'react';
import { Button } from '../components/ui/button';
import { 
  Smartphone, Download, X, Share, Plus, 
  MoreVertical, Chrome, Apple, CheckCircle
} from 'lucide-react';

/**
 * PWA Install Prompt Component
 * Shows platform-specific instructions for installing the app
 */
export default function InstallPrompt({ onDismiss, showAlways = false }) {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [isStandalone, setIsStandalone] = useState(false);
  const [isIos, setIsIos] = useState(false);
  const [isAndroid, setIsAndroid] = useState(false);
  const [showPrompt, setShowPrompt] = useState(showAlways);
  const [installed, setInstalled] = useState(false);

  useEffect(() => {
    // Check if already installed as PWA
    const standalone = window.matchMedia('(display-mode: standalone)').matches || 
                       window.navigator.standalone === true;
    setIsStandalone(standalone);
    
    // Check device type
    const userAgent = navigator.userAgent.toLowerCase();
    const isIosDevice = /iphone|ipad|ipod/.test(userAgent) && !window.MSStream;
    const isAndroidDevice = /android/.test(userAgent);
    
    setIsIos(isIosDevice);
    setIsAndroid(isAndroidDevice);
    
    // Check if already dismissed recently
    if (!showAlways) {
      const dismissed = localStorage.getItem('pwa-prompt-dismissed');
      const dismissedTime = dismissed ? parseInt(dismissed) : 0;
      const daysSinceDismissed = (Date.now() - dismissedTime) / (1000 * 60 * 60 * 24);
      
      if (!standalone && daysSinceDismissed > 3) {
        setTimeout(() => setShowPrompt(true), 1500);
      }
    }
    
    // Listen for beforeinstallprompt (Chrome/Android/Edge)
    const handleBeforeInstall = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };
    
    // Listen for successful install
    const handleAppInstalled = () => {
      setInstalled(true);
      setShowPrompt(false);
      localStorage.setItem('pwa-installed', 'true');
    };
    
    window.addEventListener('beforeinstallprompt', handleBeforeInstall);
    window.addEventListener('appinstalled', handleAppInstalled);
    
    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstall);
      window.removeEventListener('appinstalled', handleAppInstalled);
    };
  }, [showAlways]);

  const handleInstallClick = async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      if (outcome === 'accepted') {
        setInstalled(true);
      }
      setDeferredPrompt(null);
    }
  };

  const dismissPrompt = () => {
    localStorage.setItem('pwa-prompt-dismissed', Date.now().toString());
    setShowPrompt(false);
    if (onDismiss) onDismiss();
  };

  // Don't show if already installed
  if (isStandalone || installed) {
    return null;
  }

  if (!showPrompt) {
    return null;
  }

  return (
    <div className="fixed bottom-4 left-4 right-4 md:left-auto md:right-4 md:w-[420px] z-50 animate-in slide-in-from-bottom-4 duration-300">
      <div className="bg-card/95 backdrop-blur-xl border border-accent/30 rounded-2xl p-5 shadow-2xl shadow-accent/10">
        {/* Close button */}
        <button 
          onClick={dismissPrompt}
          className="absolute top-3 right-3 p-1.5 rounded-full hover:bg-secondary/50 transition-colors"
        >
          <X className="w-4 h-4 text-muted-foreground" />
        </button>
        
        {/* Header */}
        <div className="flex items-start gap-4 mb-4">
          <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-accent/30 to-primary/30 flex items-center justify-center flex-shrink-0 border border-accent/20">
            <Smartphone className="w-7 h-7 text-accent" />
          </div>
          <div>
            <h3 className="font-bold text-lg text-foreground">Install Leader Hub</h3>
            <p className="text-sm text-muted-foreground">Add to home screen for the best experience</p>
          </div>
        </div>

        {/* iOS Instructions */}
        {isIos && (
          <div className="bg-secondary/40 rounded-xl p-4 mb-4">
            <div className="flex items-center gap-2 mb-3">
              <Apple className="w-5 h-5 text-white" />
              <span className="font-medium">Install on iPhone/iPad</span>
            </div>
            
            <div className="space-y-3">
              {/* Step 1 */}
              <div className="flex items-start gap-3">
                <div className="w-6 h-6 rounded-full bg-accent/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <span className="text-xs font-bold text-accent">1</span>
                </div>
                <div className="flex-1">
                  <p className="text-sm">
                    Tap the <span className="inline-flex items-center gap-1 bg-blue-500/20 px-2 py-0.5 rounded text-blue-400">
                      <Share className="w-3.5 h-3.5" /> Share
                    </span> button at the bottom of Safari
                  </p>
                </div>
              </div>
              
              {/* Step 2 */}
              <div className="flex items-start gap-3">
                <div className="w-6 h-6 rounded-full bg-accent/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <span className="text-xs font-bold text-accent">2</span>
                </div>
                <div className="flex-1">
                  <p className="text-sm">
                    Scroll down and tap <span className="inline-flex items-center gap-1 bg-secondary px-2 py-0.5 rounded">
                      <Plus className="w-3.5 h-3.5" /> Add to Home Screen
                    </span>
                  </p>
                </div>
              </div>
              
              {/* Step 3 */}
              <div className="flex items-start gap-3">
                <div className="w-6 h-6 rounded-full bg-accent/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <span className="text-xs font-bold text-accent">3</span>
                </div>
                <div className="flex-1">
                  <p className="text-sm">
                    Tap <span className="text-accent font-medium">Add</span> in the top right corner
                  </p>
                </div>
              </div>
            </div>
            
            <div className="mt-3 pt-3 border-t border-border/30 text-xs text-muted-foreground">
              <p>⚠️ Must use Safari browser for installation</p>
            </div>
          </div>
        )}

        {/* Android Instructions */}
        {isAndroid && !deferredPrompt && (
          <div className="bg-secondary/40 rounded-xl p-4 mb-4">
            <div className="flex items-center gap-2 mb-3">
              <Chrome className="w-5 h-5 text-green-400" />
              <span className="font-medium">Install on Android</span>
            </div>
            
            <div className="space-y-3">
              {/* Step 1 */}
              <div className="flex items-start gap-3">
                <div className="w-6 h-6 rounded-full bg-accent/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <span className="text-xs font-bold text-accent">1</span>
                </div>
                <div className="flex-1">
                  <p className="text-sm">
                    Tap the <span className="inline-flex items-center gap-1 bg-secondary px-2 py-0.5 rounded">
                      <MoreVertical className="w-3.5 h-3.5" /> Menu
                    </span> (3 dots) in Chrome
                  </p>
                </div>
              </div>
              
              {/* Step 2 */}
              <div className="flex items-start gap-3">
                <div className="w-6 h-6 rounded-full bg-accent/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <span className="text-xs font-bold text-accent">2</span>
                </div>
                <div className="flex-1">
                  <p className="text-sm">
                    Tap <span className="inline-flex items-center gap-1 bg-secondary px-2 py-0.5 rounded">
                      <Download className="w-3.5 h-3.5" /> Add to Home screen
                    </span> or <span className="text-accent">Install app</span>
                  </p>
                </div>
              </div>
              
              {/* Step 3 */}
              <div className="flex items-start gap-3">
                <div className="w-6 h-6 rounded-full bg-accent/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <span className="text-xs font-bold text-accent">3</span>
                </div>
                <div className="flex-1">
                  <p className="text-sm">
                    Tap <span className="text-accent font-medium">Install</span> to confirm
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Android with Install Prompt Available */}
        {isAndroid && deferredPrompt && (
          <div className="bg-green-500/10 rounded-xl p-4 mb-4 border border-green-500/30">
            <div className="flex items-center gap-2 text-green-400">
              <CheckCircle className="w-5 h-5" />
              <span className="font-medium">Ready to install!</span>
            </div>
            <p className="text-sm text-muted-foreground mt-1">
              Click the button below to add Leader Hub to your home screen
            </p>
          </div>
        )}

        {/* Desktop/Other */}
        {!isIos && !isAndroid && (
          <div className="bg-secondary/40 rounded-xl p-4 mb-4">
            <div className="flex items-center gap-2 mb-3">
              <Chrome className="w-5 h-5 text-blue-400" />
              <span className="font-medium">Install on Desktop</span>
            </div>
            <p className="text-sm text-muted-foreground">
              Look for the install icon <Download className="w-4 h-4 inline" /> in your browser's address bar, 
              or use the menu to "Install Leader Hub"
            </p>
          </div>
        )}

        {/* Benefits */}
        <div className="flex flex-wrap gap-2 mb-4">
          <span className="px-2 py-1 bg-accent/10 rounded-lg text-xs text-accent">⚡ Fast loading</span>
          <span className="px-2 py-1 bg-accent/10 rounded-lg text-xs text-accent">📱 Native feel</span>
          <span className="px-2 py-1 bg-accent/10 rounded-lg text-xs text-accent">🔔 Notifications</span>
        </div>
        
        {/* Action buttons */}
        <div className="flex gap-2">
          <Button 
            variant="ghost" 
            className="flex-1 text-muted-foreground"
            onClick={dismissPrompt}
          >
            Maybe Later
          </Button>
          
          {/* Show Install button only if we have the deferred prompt (Android/Chrome) */}
          {deferredPrompt && (
            <Button 
              className="flex-1 bg-accent hover:bg-accent/90 text-accent-foreground gap-2"
              onClick={handleInstallClick}
            >
              <Download className="w-4 h-4" />
              Install Now
            </Button>
          )}
          
          {/* For iOS, just acknowledge */}
          {isIos && (
            <Button 
              className="flex-1 bg-accent hover:bg-accent/90 text-accent-foreground"
              onClick={dismissPrompt}
            >
              Got It!
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}

/**
 * Simple Install Button for Settings page
 */
export function InstallButton() {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [isStandalone, setIsStandalone] = useState(false);
  const [showInstructions, setShowInstructions] = useState(false);

  useEffect(() => {
    const standalone = window.matchMedia('(display-mode: standalone)').matches || 
                       window.navigator.standalone === true;
    setIsStandalone(standalone);
    
    const handleBeforeInstall = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };
    
    window.addEventListener('beforeinstallprompt', handleBeforeInstall);
    return () => window.removeEventListener('beforeinstallprompt', handleBeforeInstall);
  }, []);

  const handleInstall = async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      await deferredPrompt.userChoice;
      setDeferredPrompt(null);
    } else {
      setShowInstructions(true);
    }
  };

  if (isStandalone) {
    return (
      <div className="flex items-center gap-2 text-green-400">
        <CheckCircle className="w-5 h-5" />
        <span>App is installed!</span>
      </div>
    );
  }

  return (
    <>
      <Button onClick={handleInstall} className="gap-2">
        <Download className="w-4 h-4" />
        {deferredPrompt ? 'Install App' : 'How to Install'}
      </Button>
      
      {showInstructions && (
        <InstallPrompt 
          showAlways={true} 
          onDismiss={() => setShowInstructions(false)} 
        />
      )}
    </>
  );
}
